import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  HubPage, Container, SectionHead, CategoryCard, MomentCard, ResourceCard, FeaturedCard,
  SearchBox, BetaCTA, Band, HeroMedia, NewsletterSignup, Seo,
} from "../components/hub/HubKit";
import {
  CATEGORIES, LIFE_MOMENTS, CONTENT_TYPES, RESOURCES, searchIndex, PAGE_IMAGES,
  featuredResources, recentResources, popularResources, resourcesByType, typeLabel, COMMUNITY_STORIES,
} from "../data/hub";
import { track } from "../lib/analytics";

const Row = ({ children }) => (
  <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
);

export default function Explore() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = (params.get("q") || "").trim().toLowerCase();
  const activeType = params.get("type") || "";

  const onSearch = (e) => {
    e.preventDefault();
    const val = new FormData(e.currentTarget).get("q");
    if (val && String(val).trim()) track("search_used", String(val).trim());
    navigate(`/explore?q=${encodeURIComponent(val || "")}`);
  };

  const results = q ? RESOURCES.filter((r) => searchIndex(r).includes(q)) : [];
  const catResults = q ? CATEGORIES.filter((c) => `${c.title} ${c.blurb}`.toLowerCase().includes(q)) : [];
  const typeItems = activeType ? resourcesByType(activeType) : [];
  const featured = featuredResources();

  return (
    <HubPage testid="explore-page">
      <Seo
        title="Explore HEARD — Browse Resources by Category, Life Moment & Type"
        description="Discover HEARD's everyday-life resources. Browse by category, life moment, or content type, and find featured, recent, and popular guides."
      />

      {/* Hero + search */}
      <Container className="pt-14 pb-8 sm:pt-20">
        <div className="grid items-center gap-8 lg:grid-cols-[1fr_0.82fr]">
          <div>
            <SectionHead overline="Explore" title="Find what you need." sub="Search our resources, or browse by category, life moment, and content type." />
            <div className="mt-8 max-w-xl"><SearchBox defaultValue={params.get("q") || ""} onSubmit={onSearch} autoFocus /></div>
          </div>
          <HeroMedia src={PAGE_IMAGES.explore} alt="A calm, light-filled living space with soft natural daylight" eager className="aspect-[16/10] lg:aspect-[5/4]" />
        </div>
      </Container>

      {q && (
        <Container className="pb-20" >
          <div data-testid="search-results">
            <p className="text-sm uppercase tracking-[0.14em] text-taupe">
              {results.length + catResults.length} result{results.length + catResults.length === 1 ? "" : "s"} for "{q}"
            </p>
            {catResults.length > 0 && (
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {catResults.map((c) => <CategoryCard key={c.slug} cat={c} />)}
              </div>
            )}
            {results.length > 0 && <Row>{results.map((r) => <ResourceCard key={r.slug} resource={r} />)}</Row>}
            {results.length === 0 && catResults.length === 0 && (
              <p className="mt-6 text-ink/70" data-testid="no-results">No matches yet — try a broader word like "sleep", "money", or "grief".</p>
            )}
          </div>
        </Container>
      )}

      {!q && (
        <>
          {/* Featured — large editorial treatment */}
          <Band tone="white" className="border-y border-line/60 py-14 sm:py-20">
            <Container>
              <div data-testid="featured-resources">
                <h3 className="serif-display text-2xl text-charcoal">Featured resources</h3>
                {featured[0] && <div className="mt-6"><FeaturedCard resource={featured[0]} /></div>}
                {featured.length > 1 && (
                  <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {featured.slice(1).map((r) => <ResourceCard key={r.slug} resource={r} />)}
                  </div>
                )}
              </div>
            </Container>
          </Band>

          {/* Browse by content type */}
          <Container className="py-14 sm:py-20">
            <div data-testid="browse-by-type">
              <h3 className="serif-display text-2xl text-charcoal">Browse by content type</h3>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <button
                  onClick={() => navigate("/explore")}
                  data-testid="type-filter-all"
                  className={`rounded-full border px-4 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.16em] transition-colors ${
                    !activeType ? "border-sage-deep bg-sage-deep text-cream" : "border-line bg-white/70 text-sage-deep hover:border-sage"
                  }`}
                >
                  All
                </button>
                {CONTENT_TYPES.filter((t) => t.key !== "finds").map((t) => (
                  <button
                    key={t.key}
                    onClick={() => navigate(`/explore?type=${t.key}`)}
                    data-testid={`type-filter-${t.key}`}
                    className={`rounded-full border px-4 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.16em] transition-colors ${
                      activeType === t.key ? "border-sage-deep bg-sage-deep text-cream" : "border-line bg-white/70 text-sage-deep hover:border-sage"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {activeType && (
                <div data-testid="type-results">
                  <p className="mt-6 text-sm uppercase tracking-[0.14em] text-taupe">{typeItems.length} in {typeLabel(activeType)}</p>
                  <Row>{typeItems.map((r) => <ResourceCard key={r.slug} resource={r} />)}</Row>
                </div>
              )}
            </div>
          </Container>

          {/* Recently added + Popular */}
          <Band tone="deep" className="py-14 sm:py-20">
            <Container>
              <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
                <div data-testid="recent-resources">
                  <h3 className="serif-display text-2xl text-charcoal">Recently added</h3>
                  <div className="mt-6 space-y-5">
                    {recentResources(3).map((r) => <ResourceCard key={r.slug} resource={r} />)}
                  </div>
                </div>
                <div data-testid="popular-resources">
                  <h3 className="serif-display text-2xl text-charcoal">Popular — worth exploring</h3>
                  <div className="mt-6 space-y-5">
                    {popularResources().slice(0, 3).map((r) => <ResourceCard key={r.slug} resource={r} />)}
                  </div>
                </div>
              </div>
            </Container>
          </Band>

          {/* Browse categories */}
          <Container className="py-14 sm:py-20">
            <div data-testid="browse-categories">
              <h3 className="serif-display text-2xl text-charcoal">Browse categories</h3>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {CATEGORIES.map((c) => <CategoryCard key={c.slug} cat={c} />)}
              </div>
            </div>
          </Container>

          {/* Life moments */}
          <Band tone="sage" className="border-y border-line/60 py-14 sm:py-20">
            <Container>
              <div data-testid="browse-life-moments">
                <span className="text-[0.7rem] font-medium uppercase tracking-[0.28em] text-sage">Wherever you are</span>
                <h3 className="serif-display mt-3 text-2xl sm:text-3xl text-charcoal">Explore by life moment</h3>
                <p className="mt-3 max-w-xl text-base leading-relaxed text-ink/70">Start from how life feels right now — we'll point you somewhere gentle.</p>
                <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {LIFE_MOMENTS.map((m) => <MomentCard key={m.label} moment={m} />)}
                </div>
              </div>
            </Container>
          </Band>

          {/* From the Community — subtle editorial feature (not a resource category) */}
          {COMMUNITY_STORIES[0] && (
            <Band tone="sage" className="border-y border-line/60 py-14 sm:py-16">
              <Container>
                <div data-testid="explore-community-story">
                  <span className="text-[0.7rem] font-medium uppercase tracking-[0.28em] text-[#B98A4E]">From the Community</span>
                  <Link
                    to={`/community-stories/${COMMUNITY_STORIES[0].slug}`}
                    onClick={() => track("story_opened", `explore-${COMMUNITY_STORIES[0].slug}`)}
                    data-testid="explore-story-card"
                    className="group mt-4 flex flex-col overflow-hidden rounded-[1.6rem] border border-line bg-white/75 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(47,75,57,0.1)] sm:flex-row"
                  >
                    {COMMUNITY_STORIES[0].image && (
                      <div className="overflow-hidden bg-cream-deep/40 sm:w-2/5">
                        <img src={COMMUNITY_STORIES[0].image} alt={COMMUNITY_STORIES[0].subtitle} loading="lazy" className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-[1.04] sm:h-full" />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col justify-center p-6 sm:p-8">
                      <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-sage">HEARD Community Story</span>
                      <h3 className="serif-display mt-2 text-2xl text-charcoal">{COMMUNITY_STORIES[0].title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-ink/70">{COMMUNITY_STORIES[0].excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sage-deep group-hover:gap-2.5 transition-all">Read the story →</span>
                    </div>
                  </Link>
                </div>
              </Container>
            </Band>
          )}

          {/* Newsletter */}
          <Band tone="white" className="border-y border-line/60 py-14 sm:py-20">
            <Container><NewsletterSignup source="explore" /></Container>
          </Band>

          <Container className="py-14 sm:py-20"><BetaCTA /></Container>
        </>
      )}
    </HubPage>
  );
}
